import clsx from 'clsx'
import { categoryLabel, categoryTagClass } from '../utils/format'

/* ── StatCard ── */
interface StatCardProps {
  label: string; value: string; sub?: string; accent?: string; highlight?: boolean
}
export function StatCard({ label, value, sub, accent = '#0096c7', highlight }: StatCardProps) {
  return (
    <div className="stat-card" style={{ '--accent': accent } as React.CSSProperties}>
      <p className="stat-label">{label}</p>
      <p className="stat-value mono">{value}</p>
      {sub && <p className="stat-sub">{sub}</p>}
    </div>
  )
}

/* ── StatusBadge ── */
export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { cls: string; label: string }> = {
    processing: { cls: 'badge-processing', label: 'Processing' },
    done:       { cls: 'badge-done',       label: 'Complete'   },
    error:      { cls: 'badge-error',      label: 'Error'      },
    queued:     { cls: 'badge-queued',     label: 'Queued'     },
  }
  const { cls, label } = map[status] ?? map.queued
  return (
    <span className={`badge ${cls}`}>
      <span className="dot-pulse" />
      {label}
    </span>
  )
}

/* ── CategoryTag ── */
export function CategoryTag({ category }: { category: string }) {
  return <span className={`tag ${categoryTagClass(category)}`}>{categoryLabel(category)}</span>
}

/* ── CountryBar ── */
const BAR_COLORS: Record<string, string> = {
  domestic:      '#0096c7',
  foreign_eu:    '#7c3aed',
  foreign_noneu: '#ea580c',
}
export function CountryBar({ country, value, max, formatted, category }: {
  country: string; value: number; max: number; formatted: string; category: string
}) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0
  const color = BAR_COLORS[category] ?? '#0096c7'
  return (
    <div className="country-row">
      <div className="country-name">{country.length > 13 ? country.slice(0, 13) + '…' : country}</div>
      <div className="country-bar-track">
        <div className="country-bar-fill" style={{ width: pct + '%', background: color }} />
      </div>
      <div className="country-val">{formatted}</div>
    </div>
  )
}

/* ── Spinner ── */
export function Spinner({ size = 28 }: { size?: number }) {
  return <div className="spinner" style={{ width: size, height: size }} />
}

/* ── Empty ── */
export function Empty({ icon, title, sub, action }: {
  icon?: React.ReactNode; title: string; sub?: string; action?: React.ReactNode
}) {
  return (
    <div className="empty-state">
      {icon && <div className="empty-icon">{icon}</div>}
      <p className="empty-title">{title}</p>
      {sub && <p className="empty-sub">{sub}</p>}
      {action}
    </div>
  )
}

/* ── Section header ── */
export function SectionHeader({ title, sub, action }: {
  title: string; sub?: string; action?: React.ReactNode
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18 }}>
      <div>
        <h2 className="section-title">{title}</h2>
        {sub && <p className="section-sub">{sub}</p>}
      </div>
      {action}
    </div>
  )
}

/* ── Progress split ── */
export function ProgressSplit({ segments, total }: {
  segments: { label: string; value: number; color: string }[]; total: number
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {segments.map(s => {
        const pct = total > 0 ? (s.value / total) * 100 : 0
        return (
          <div key={s.label}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 5 }}>
              <span style={{ color: 'var(--text-2)' }}>{s.label}</span>
              <span className="mono" style={{ color: s.color, fontWeight: 600 }}>{pct.toFixed(1)}%</span>
            </div>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: pct + '%', background: s.color }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

/* ── InfoBox ── */
export function InfoBox({ children, variant = 'blue' }: {
  children: React.ReactNode; variant?: 'blue' | 'green' | 'amber'
}) {
  return <div className={`info-box info-box-${variant}`}>{children}</div>
}
